/* External */
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import isEqual from 'date-fns/isEqual';
import isSameDay from 'date-fns/isSameDay';
import isToday from 'date-fns/isToday';
import isTomorrow from 'date-fns/isTomorrow';
import isWithinInterval from 'date-fns/isWithinInterval';
import setMilliseconds from 'date-fns/setMilliseconds';
import setSeconds from 'date-fns/setSeconds';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import subHours from 'date-fns/subHours';
import addSeconds from 'date-fns/addSeconds';
import { formatInTimeZone } from 'date-fns-tz';

/* Ott-common */
import { DEFAULT_FORMAT_TIME_TEXTS } from '@dtvgo/constants';

/* Constants */
const DEFAULT_FORMAT_TIME = 'HH:mm';

/**
 * Method that return a text time formatted
 * @param {number} durationInMinutes number of duration in minutes.
 * @returns timeFormatted
 */
export function formatDuration(durationInSeconds) {
  const durationInMinutes = durationInSeconds / 60;
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = Math.floor(durationInMinutes) - (hours * 60);
  const hoursSuffix = hours > 1 ? 'hs' : 'h';
  const hoursText = hours >= 1 ? `${hours} ${hoursSuffix}` : '';
  const minutesText = minutes > 0 ? `${minutes} min` : '';

  return `${hoursText} ${minutesText}`.trim();
}

/**
 * Returns a string with the correct format of the date in the given timeZone.
 * App-side port of @dtvgo/date-utils: the timezone branch used moment-timezone
 * (kept out of the boot bundle); date-fns-tz produces byte-identical output for
 * the app's only timezone format (`HH:mm`) and falls back to `format` on an
 * invalid timezone, matching the original moment branch.
 * @param {date} date - The date starts content.
 * @param {string} formatString - format to implement
 * @param {string} timeZone - timeZone to display
 * @returns {string} The formatted time
 */
export function formatDate(date, formatString, timeZone) {
  if (timeZone) {
    try {
      return formatInTimeZone(date, formatString, timeZone);
    } catch {
      return format(date, formatString);
    }
  }
  return format(date, formatString);
}

/**
 * @method formatDifference
 * @description Method that format difference date as xxd xxh xxmins.
 * @param {Date} currentTime opcional, for default is current date.
 * @param {Date} endDate
 */
// eslint-disable-next-line max-params -- port of @dtvgo/date-utils signature
export function formatTimeDifference(
  currentTime,
  endDate,
  formatTimeTexts,
  validateStartedEndedRightNow,
) {
  if (endDate) {
    const start = currentTime || new Date();
    const timeTexts = formatTimeTexts || DEFAULT_FORMAT_TIME_TEXTS;
    let end = endDate;
    const resultText = [];
    const {
      minutes,
      hours: { plural: txtHours, singular: txtHour },
    } = timeTexts.time;

    const diffInDay = differenceInDays(end, start);
    if (diffInDay > 0) {
      resultText.push(`${diffInDay} d`);
      end = subDays(end, diffInDay);
    }
    const diffInHours = differenceInHours(end, start);
    if (diffInHours > 0) {
      resultText.push(`${diffInHours} ${diffInHours > 1 ? txtHours : txtHour}`);
      end = subHours(end, diffInHours);
    }
    const diffInSeconds = differenceInSeconds(end, start);
    const diffInMinutes = Math.ceil(diffInSeconds / 60);
    if (diffInMinutes > 0) {
      resultText.push(`${diffInMinutes} ${minutes}`);
    } else if (validateStartedEndedRightNow && diffInSeconds < 60) {
      resultText.push(`0 ${minutes}`);
    }
    return resultText.join(' ');
  }
  return '';
}

// eslint-disable-next-line sonarjs/cognitive-complexity, max-params -- port of @dtvgo/date-utils
export function formatPastOrFutureTime(
  paramCurrentTime,
  startTime,
  endTime,
  formatTimeTexts,
  validateStartedEndedRightNow,
  timeZone,
  showFinishedText,
) {
  if (startTime) {
    const currentTime = paramCurrentTime || new Date();
    const {
      texts: { startsAt, finished },
      time: { day: { today, tomorrow } },
    } = formatTimeTexts || DEFAULT_FORMAT_TIME_TEXTS;

    const textTime = formatDate(startTime, DEFAULT_FORMAT_TIME, timeZone);

    if (endTime < currentTime && showFinishedText) {
      return finished;
    }

    if (endTime < currentTime && isSameDay(endTime, currentTime)) { // Past event
      return `${today} ${textTime}`; // Today XX:XX
    }

    if (startTime <= currentTime && currentTime <= endTime) { // Current event
      return '';
    }

    // Future event
    const diffInHours = differenceInHours(startTime, currentTime);
    if (diffInHours <= 0 && isSameDay(startTime, currentTime)) {
      return `${startsAt} ${formatTimeDifference(currentTime, startTime, formatTimeTexts, validateStartedEndedRightNow)}`;
    }

    if (isSameDay(startTime, currentTime)) { // Is today
      return `${today} ${textTime}`; // Today XX:XX
    }

    if (differenceInCalendarDays(startTime, currentTime) === 1) { // Is tomorrow
      return `${tomorrow} ${textTime}`; // Tomorrow XX:XX
    }

    // Is not today or tomorrow
    const dayOfWeek = formatDate(startTime, 'EEEE', timeZone);
    const textDay = formatTimeTexts.time.day[dayOfWeek.toLowerCase()];
    const textDayCapitalized = textDay.charAt(0).toUpperCase() + textDay.slice(1);
    return `${textDayCapitalized} ${textTime}`; // Weekday XX:XX
  }
  return '';
}

export function isBeforeSameOrAfterDate(date) {
  if (!date) return undefined;
  const currentDate = setSeconds(setMilliseconds(new Date(), 0), 0);
  const dateToValidate = setSeconds(setMilliseconds(date, 0), 0);
  if (isBefore(dateToValidate, currentDate)) {
    return -1;
  }
  if (isEqual(currentDate, dateToValidate)) {
    return 0;
  }
  return 1;
}

export function isWithinIntervalDate(startDate, endDate, dateToValidate = new Date()) {
  if (startDate && endDate) {
    return isWithinInterval(dateToValidate, { start: startDate, end: endDate });
  }
  return undefined;
}

export function isTodayOrLater(
  date,
  formatTexts,
) {
  const formatTimeTexts = formatTexts || DEFAULT_FORMAT_TIME_TEXTS;
  const day = new Date(date);
  const formatDay = format(day, 'i');
  const {
    today,
    tomorrow,
    monday,
    tuesday,
    wednesday,
    thursday,
    friday,
    saturday,
    sunday,
  } = formatTimeTexts.time.day;

  if (isToday(day)) {
    return today;
  }

  if (isTomorrow(day)) {
    return tomorrow;
  }

  switch (formatDay) {
    case '1': {
      return monday;
    }
    case '2': {
      return tuesday;
    }
    case '3': {
      return wednesday;
    }
    case '4': {
      return thursday;
    }
    case '5': {
      return friday;
    }
    case '6': {
      return saturday;
    }
    case '7': {
      return sunday;
    }
    default: {
      return '';
    }
  }
}

export function isTodayOrTomorrowDay(
  date,
  formatTexts,
) {
  const formatTimeTexts = formatTexts || DEFAULT_FORMAT_TIME_TEXTS;
  const day = new Date(date);

  const {
    today,
    tomorrow,
  } = formatTimeTexts.time.day;

  if (isToday(day)) {
    return today;
  }

  if (isTomorrow(day)) {
    return tomorrow;
  }

  return '';
}

export function formatTime(dateTime, formatToUse = DEFAULT_FORMAT_TIME, timeZone = undefined) {
  return formatDate(new Date(dateTime), formatToUse, timeZone);
}

export function formatStartTime(
  startTime,
  formatToUse = DEFAULT_FORMAT_TIME,
  timeZone = undefined,
) {
  if (startTime) {
    return formatTime(startTime, formatToUse, timeZone);
  }

  return '';
}

// eslint-disable-next-line max-params -- port of @dtvgo/date-utils signature
export function formatStartAndEndTime(
  startTime,
  endTime,
  formatToUse = DEFAULT_FORMAT_TIME,
  timeZone = undefined,
) {
  if (startTime && endTime) {
    return `${formatTime(startTime, formatToUse, timeZone)} - ${formatTime(endTime, formatToUse, timeZone)}`;
  }

  return '';
}

// eslint-disable-next-line max-params -- port of @dtvgo/date-utils signature
export function startsAtOrEndsInText(
  startTime,
  endTime,
  formatTexts = DEFAULT_FORMAT_TIME_TEXTS,
  timeZone = undefined,
) {
  if (!startTime) {
    return '';
  }

  const currentDate = setSeconds(setMilliseconds(new Date(), 0), 0);

  if (isBeforeSameOrAfterDate(startTime) === -1) {
    return (endTime && `${formatTexts?.texts?.endsIn || ''} ${formatTimeDifference(currentDate, endTime, undefined, true)}`) || '';
  }

  if (differenceInHours(startTime, currentDate) < 1) {
    return `${formatTexts?.texts?.startsAt || ''} ${formatTimeDifference(currentDate, startTime, undefined, true)}`;
  }

  const day = isTodayOrLater(startTime, formatTexts);

  return `${day} ${formatDate(startTime, DEFAULT_FORMAT_TIME, timeZone)}`;
}

/**
 * Returns a string with the correct format. If time is greater or equal than
 * 1 hour the format will be H:mm:ss if not, it will be mm:ss
 * @param {number} time - The time to format in seconds
 * @returns {string} The formatted time
 */
/**
 * @method getRemainingTimeFormated
 * @description Method that return the remaining time text with the correct format.
 * @param {number} currentTime
 * @param {number} duration
 */
export function getRemainingTimeFormated(currentTime, duration) {
  const timeLeft = (duration ?? 0) - (currentTime ?? 0);
  return formatSeconds(timeLeft);
}

/**
 * Returns a string with the correct format. If time is greater or equal than
 * 1 hour the format will be H:mm:ss if not, it will be mm:ss
 * @param {number} time - The time to format in seconds
 * @returns {string} The formatted time
 */
export function formatSeconds(time) {
  const dateFormat = time >= 3600 ? 'H:mm:ss' : 'mm:ss';
  if ((time || time === 0) && time !== Number.POSITIVE_INFINITY) {
    return format(addSeconds(startOfDay(new Date()), time), dateFormat);
  }
  return DEFAULT_FORMAT_TIME;
}
